import { useRef } from 'react';
import {
    useMutation,
    useQueryClient,
    UseMutationOptions,
    UseMutationResult,
    MutateOptions,
    QueryKey,
} from 'react-query';
import {
    MutationMode,
    undoableEventEmitter,
    useDataProvider,
    DataProvider,
} from 'react-admin';

import {
    DeleteBranchParams,
    DeleteBranchResult,
    GetChildNodesResult,
    GetRootNodesResult,
    GetTreeResult,
    TreeDataProvider,
    TreeRecord,
} from '../types';

/**
 * Get a callback to call the dataProvider.deleteBranch() method, the result and the loading state.
 *
 * @param {string} resource
 * @param {Params} params The deleteBranch parameters { id, previousData }
 * @param {Object} options Options object to pass to the queryClient.
 * May include side effects to be executed upon success or failure, e.g. { onSuccess: () => { refresh(); } }
 * May include a mutation mode (optimistic/pessimistic/undoable), e.g. { mutationMode: 'undoable' }
 *
 * @typedef Params
 * @prop params.id The resource identifier, e.g. 123
 * @prop params.previousData The record before the update is applied
 *
 * @returns The current mutation state. Destructure as [deleteBranch, { data, error, isLoading }].
 *
 * The return value updates according to the request state:
 *
 * - initial: [deleteBranch, { isLoading: false, isIdle: true }]
 * - start:   [deleteBranch, { isLoading: true }]
 * - success: [deleteBranch, { data: [data from response], isLoading: false, isSuccess: true }]
 * - error:   [deleteBranch, { error: [error from response], isLoading: false, isError: true }]
 *
 * The deleteBranch() function must be called with a resource and a parameter object: deleteBranch(resource, { id, previousData, meta }, options)
 *
 * This hook uses react-query useMutation under the hood.
 * This means the state object contains mutate, isIdle, reset and other react-query methods.
 *
 * @see https://react-query.tanstack.com/reference/useMutation
 *
 * @example // set params when calling the deleteBranch callback
 *
 * import { useDeleteBranch } from 'react-admin';
 *
 * const DeleteButton = ({ record }) => {
 *     const [deleteBranch, { isLoading, error }] = useDeleteBranch();
 *     const handleClick = () => {
 *         deleteBranch('nodes', { id: record.id }, record)
 *     }
 *     if (error) { return <p>ERROR</p>; }
 *     return <button disabled={isLoading} onClick={handleClick}>Delete</div>;
 * };
 *
 * @example // set params when calling the hook
 *
 * import { useDeleteBranch } from 'react-admin';
 *
 * const DeleteButton = ({ record }) => {
 *     const [deleteBranch, { isLoading, error }] = useDeleteBranch('nodes', { id: record.id }, record);
 *     if (error) { return <p>ERROR</p>; }
 *     return <button disabled={isLoading} onClick={() => deleteBranch()}>Delete</button>;
 * };
 *
 * @example // TypeScript
 * const [delete, { data }] = deleteBranch<Category>('nodes', { id });
 *                    \-- data is Category
 */
export const useDeleteBranch = <RecordType extends TreeRecord = TreeRecord>(
    resource?: string,
    params: DeleteBranchParams<RecordType> = { id: undefined },
    options: UseDeleteBranchOptions<RecordType> = {}
): UseDeleteBranchResult<RecordType> => {
    const dataProvider = useDataProvider<TreeDataProvider & DataProvider>();
    const queryClient = useQueryClient();
    const { id, previousData } = params;
    const { mutationMode = 'pessimistic', ...reactMutationOptions } = options;
    const mode = useRef<MutationMode>(mutationMode);
    const paramsRef = useRef<Partial<DeleteBranchParams<RecordType>>>(params);
    const snapshot = useRef<Snapshot>([]);

    const updateCache = ({ resource, id }) => {
        // hack: only way to tell react-query not to fetch this query for the next 5 seconds
        // because setQueryData doesn't accept a stale time option
        const now = Date.now();
        const updatedAt = mode.current === 'undoable' ? now + 5 * 1000 : now;

        const removeRecord = (
            old: RecordType[],
            parentId: RecordType['id']
        ) => {
            const index = old.findIndex(
                // eslint-disable-next-line eqeqeq
                record => record.id == parentId
            );
            if (index === -1) {
                return old;
            }
            const record = old[index];
            let newRecords = [...old.slice(0, index), ...old.slice(index + 1)];
            const children = record.children || [];
            for (const childId of children) {
                newRecords = removeRecord(newRecords, childId);
            }
            return newRecords;
        };

        const updateCollection = (old: RecordType[]) => {
            const newRecords = removeRecord(old, id);
            return newRecords.map(record => ({
                ...record,
                children: record.children
                    ? record.children.filter(childId => childId !== id)
                    : record.children,
            }));
        };

        queryClient.setQueriesData(
            [resource, 'getTree'],
            (res: GetTreeResult<RecordType>['data']) => {
                if (!res) return res;
                const newCollection = updateCollection(res);
                const recordWasFound = newCollection.length < res.length;
                return recordWasFound ? newCollection : res;
            },
            { updatedAt }
        );
        queryClient.setQueriesData(
            [resource, 'getRootNodes'],
            (res: GetRootNodesResult<RecordType>['data']) =>
                res && res.length > 0 ? updateCollection(res) : res,
            { updatedAt }
        );
        queryClient.setQueriesData(
            [resource, 'getChildNodes'],
            (res: GetChildNodesResult<RecordType>['data']) => {
                if (!res) return res;
                const newCollection = updateCollection(res);
                const recordWasFound = newCollection.length < res.length;
                return recordWasFound ? newCollection : res;
            },
            { updatedAt }
        );
    };

    const mutation = useMutation<
        DeleteBranchResult<RecordType>['data'],
        Error,
        Partial<UseDeleteBranchMutateParams<RecordType>>
    >(
        ({
            resource: callTimeResource = resource,
            id: callTimeId = paramsRef.current.id,
            previousData: callTimePreviousData = paramsRef.current.previousData,
            meta: callTimeMeta = paramsRef.current.meta,
        } = {}) =>
            dataProvider
                .deleteBranch<RecordType>(callTimeResource, {
                    id: callTimeId,
                    previousData: callTimePreviousData,
                    meta: callTimeMeta,
                })
                .then(({ data }) => data),
        {
            ...reactMutationOptions,
            onMutate: async variables => {
                if (reactMutationOptions.onMutate) {
                    const userContext =
                        (await reactMutationOptions.onMutate(variables)) || {};
                    return {
                        snapshot: snapshot.current,
                        // @ts-ignore
                        ...userContext,
                    };
                } else {
                    // Return a context object with the snapshot value
                    return { snapshot: snapshot.current };
                }
            },
            onError: (error, variables, context: { snapshot: Snapshot }) => {
                if (
                    mode.current === 'optimistic' ||
                    mode.current === 'undoable'
                ) {
                    // If the mutation fails, use the context returned from onMutate to rollback
                    context.snapshot.forEach(([key, value]) => {
                        queryClient.setQueryData(key, value);
                    });
                }

                if (reactMutationOptions.onError) {
                    return reactMutationOptions.onError(
                        error,
                        variables,
                        context
                    );
                }
                // call-time error callback is executed by react-query
            },
            onSuccess: (data, variables = {}, context) => {
                if (mode.current === 'pessimistic') {
                    // update the getOne and getList query cache with the new result
                    const {
                        resource: callTimeResource = resource,
                        id: callTimeId = id,
                    } = variables;
                    updateCache({
                        resource: callTimeResource,
                        id: callTimeId,
                    });

                    if (reactMutationOptions.onSuccess) {
                        reactMutationOptions.onSuccess(
                            data,
                            variables,
                            context
                        );
                    }
                    // call-time success callback is executed by react-query
                }
            },
            onSettled: (
                data,
                error,
                variables = {},
                context: { snapshot: Snapshot }
            ) => {
                if (
                    mode.current === 'optimistic' ||
                    mode.current === 'undoable'
                ) {
                    // Always refetch after error or success:
                    context.snapshot.forEach(([key]) => {
                        queryClient.invalidateQueries(key);
                    });
                }

                if (reactMutationOptions.onSettled) {
                    return reactMutationOptions.onSettled(
                        data,
                        error,
                        variables,
                        context
                    );
                }
            },
        }
    );

    const mutate = async (
        callTimeResource: string = resource,
        callTimeParams: Partial<DeleteBranchParams<RecordType>> = {},
        callTimeOptions: MutateOptions<
            RecordType,
            Error,
            Partial<UseDeleteBranchMutateParams<RecordType>>,
            unknown
        > & { mutationMode?: MutationMode } = {}
    ) => {
        const { mutationMode, onSuccess, onSettled, onError } = callTimeOptions;

        // store the hook time params *at the moment of the call*
        // because they may change afterwards, which would break the undoable mode
        // as the previousData would be overwritten by the optimistic update
        paramsRef.current = params;

        if (mutationMode) {
            mode.current = mutationMode;
        }

        if (mode.current === 'pessimistic') {
            return mutation.mutate(
                { resource: callTimeResource, ...callTimeParams },
                { onSuccess, onSettled, onError }
            );
        }

        const {
            id: callTimeId = id,
            previousData: callTimePreviousData = previousData,
        } = callTimeParams;

        // optimistic update as documented in https://react-query.tanstack.com/guides/optimistic-updates
        // except we do it in a mutate wrapper instead of the onMutate callback
        // to have access to success side effects

        const queryKeys = [
            [callTimeResource, 'getTree'],
            [callTimeResource, 'getRootNodes'],
            [callTimeResource, 'getChildNodes'],
        ];

        /**
         * Snapshot the previous values via queryClient.getQueriesData()
         *
         * The snapshotData ref will contain an array of tuples [query key, associated data]
         *
         * @example
         * [
         *   [['posts', 'getList'], { data: [{ id: 1, title: 'Hello' }], total: 1 }],
         *   [['posts', 'getMany'], [{ id: 1, title: 'Hello' }]],
         * ]
         *
         * @see https://react-query.tanstack.com/reference/QueryClient#queryclientgetqueriesdata
         */
        snapshot.current = queryKeys.reduce(
            (prev, curr) => prev.concat(queryClient.getQueriesData(curr)),
            [] as Snapshot
        );

        // Cancel any outgoing re-fetches (so they don't overwrite our optimistic update)
        await Promise.all(
            snapshot.current.map(([key]) => queryClient.cancelQueries(key))
        );

        // Optimistically update to the new value
        updateCache({
            resource: callTimeResource,
            id: callTimeId,
        });

        // run the success callbacks during the next tick
        if (onSuccess) {
            setTimeout(
                () =>
                    onSuccess(
                        callTimePreviousData,
                        { resource: callTimeResource, ...callTimeParams },
                        { snapshot: snapshot.current }
                    ),
                0
            );
        }
        if (reactMutationOptions.onSuccess) {
            setTimeout(
                () =>
                    reactMutationOptions.onSuccess(
                        callTimePreviousData,
                        { resource: callTimeResource, ...callTimeParams },
                        { snapshot: snapshot.current }
                    ),
                0
            );
        }

        if (mode.current === 'optimistic') {
            // call the mutate method without success side effects
            return mutation.mutate(
                { resource: callTimeResource, ...callTimeParams },
                { onSettled, onError }
            );
        } else {
            // undoable mutation: register the mutation for later
            undoableEventEmitter.once('end', ({ isUndo }) => {
                if (isUndo) {
                    // rollback
                    snapshot.current.forEach(([key, value]) => {
                        queryClient.setQueryData(key, value);
                    });
                } else {
                    // call the mutate method without success side effects
                    mutation.mutate(
                        { resource: callTimeResource, ...callTimeParams },
                        { onSettled, onError }
                    );
                }
            });
        }
    };

    return [mutate, mutation];
};

type Snapshot = [key: QueryKey, value: any][];

export interface UseDeleteBranchMutateParams<
    RecordType extends TreeRecord = TreeRecord
> {
    resource?: string;
    id?: RecordType['id'];
    data?: Partial<RecordType>;
    previousData?: RecordType;
    meta?: any;
}

export type UseDeleteBranchOptions<RecordType extends TreeRecord = TreeRecord> =
    UseMutationOptions<
        RecordType,
        Error,
        Partial<UseDeleteBranchMutateParams<RecordType>>
    > & { mutationMode?: MutationMode };

export type UseDeleteBranchResult<RecordType extends TreeRecord = TreeRecord> =
    [
        (
            resource?: string,
            params?: Partial<DeleteBranchParams<RecordType>>,
            options?: MutateOptions<
                RecordType,
                Error,
                Partial<UseDeleteBranchMutateParams<RecordType>>,
                unknown
            > & { mutationMode?: MutationMode }
        ) => Promise<void>,
        UseMutationResult<
            DeleteBranchResult<RecordType>['data'],
            Error,
            Partial<DeleteBranchParams<RecordType> & { resource?: string }>,
            unknown
        >
    ];
